import { Chain, CardGrid, Figure, LayerStack, MockFrame, Orbit } from './primitives'
import { LamsIcon, LamsLockup } from '../Brand'

/* ============================ OPENING ============================ */

/** The parcel, river and contour field. Used behind the cover and behind the hero. */
export function ParcelField() {
  return (
    <svg className="cover__field" viewBox="0 0 800 400" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
      <defs>
        <linearGradient id="riverfade" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="var(--blue-700)" stopOpacity="0.10" />
          <stop offset="100%" stopColor="var(--green-700)" stopOpacity="0.05" />
        </linearGradient>
      </defs>
      <rect width="800" height="400" fill="url(#riverfade)" />
      {/* Parcel grid */}
      <g stroke="var(--blue-700)" strokeOpacity="0.13" fill="none" strokeWidth="1">
        {Array.from({ length: 11 }, (_, i) => (
          <line key={`v${i}`} x1={i * 80} y1="0" x2={i * 80} y2="400" />
        ))}
        {Array.from({ length: 6 }, (_, i) => (
          <line key={`h${i}`} x1="0" y1={i * 80} x2="800" y2={i * 80} />
        ))}
      </g>
      {/* Irregular parcels */}
      <g stroke="var(--blue-700)" strokeOpacity="0.28" fill="var(--blue-700)" fillOpacity="0.04" strokeWidth="1.5">
        <path d="M80 80 L240 60 L280 180 L120 210 Z" />
        <path d="M320 120 L470 100 L500 230 L350 250 Z" />
        <path d="M540 180 L690 150 L720 280 L560 300 Z" />
        <path d="M140 240 L300 280 L270 370 L110 340 Z" />
      </g>
      {/* River */}
      <path
        d="M-20 300 C 120 260, 180 340, 300 300 S 480 220, 600 250 S 760 210, 820 180"
        stroke="var(--blue-700)"
        strokeOpacity="0.30"
        strokeWidth="14"
        fill="none"
        strokeLinecap="round"
      />
      <path
        d="M-20 300 C 120 260, 180 340, 300 300 S 480 220, 600 250 S 760 210, 820 180"
        stroke="var(--blue-500)"
        strokeOpacity="0.45"
        strokeWidth="4"
        fill="none"
        strokeLinecap="round"
      />
      {/* Contours */}
      <g stroke="var(--green-700)" strokeOpacity="0.18" fill="none" strokeWidth="1">
        <path d="M600 60 C 660 40, 730 70, 760 110" />
        <path d="M590 80 C 655 62, 725 92, 752 128" />
        <path d="M580 100 C 650 84, 718 114, 744 146" />
      </g>
      {/* GIS vertices on the parcels */}
      <g fill="var(--gold-700)" fillOpacity="0.55">
        {[[80,80],[240,60],[280,180],[320,120],[470,100],[540,180],[690,150],[140,240],[300,280]].map(([x,y]) => (
          <circle key={`${x}-${y}`} cx={x} cy={y} r="3.5" />
        ))}
      </g>
    </svg>
  )
}

export function Cover() {
  return (
    <Figure label="The LAMS mark — four stacked cartographic layers with a river running through them — over a parcel and river field." wide>
      <div className="cover">
        <div className="cover__lockup">
          <LamsLockup variant="stacked" tagline />
        </div>
        <ParcelField />
      </div>
    </Figure>
  )
}

export function LifecycleOverview() {
  return (
    <Figure
      label="Acquisition leads to Land Management, which leads to Disposition. GIS, work, documents, contracts, measures, reporting and audit run underneath all three as a shared layer."
      wide
    >
      <div className="lifecycle">
        <Chain
          steps={[
            { label: 'Acquisition' },
            { label: 'Land Management' },
            { label: 'Disposition' },
          ]}
        />
        <div className="lifecycle__brace" aria-hidden="true">
          <svg viewBox="0 0 600 16" preserveAspectRatio="none" width="100%" height="16">
            <path d="M4 2 v6 q0 6 6 6 h274 q6 0 6 -6 v0 q0 6 6 6 h274 q6 0 6 -6 v-6" stroke="var(--line-strong)" strokeWidth="1.5" fill="none" />
          </svg>
        </div>
        <ul className="lifecycle__shared">
          {['GIS', 'Work', 'Documents', 'Contracts', 'Measures', 'Reporting', 'Audit'].map((s) => (
            <li key={s}>{s}</li>
          ))}
        </ul>
      </div>
    </Figure>
  )
}

/* ============================ CHALLENGE ============================ */

export function OutcomeCards() {
  return (
    <Figure label="Four District outcomes: centralize, automate, geospatially enable, improve traceability.">
      <CardGrid
        columns={4}
        cards={[
          { meta: '01', title: 'Centralize', detail: 'One web-based interface in place of manual coordination across systems' },
          { meta: '02', title: 'Automate', detail: 'Electronic submissions, workflow approvals, task assignment tracking' },
          { meta: '03', title: 'Geospatially enable', detail: 'ESRI GIS integration and real-time geospatial visualization' },
          { meta: '04', title: 'Improve traceability', detail: 'Reliable reporting in place of spreadsheets and PDFs' },
        ]}
      />
    </Figure>
  )
}

const CURRENT_SYSTEMS = [
  { name: 'Access DB', note: 'Land acquisition transactions' },
  { name: 'CivicPlus', note: 'Applications received' },
  { name: 'Windows folders', note: 'Workflow routed by hand' },
  { name: 'PaperVision', note: 'Document retention' },
  { name: 'ArcGIS', note: 'Review, planning, tracking' },
  { name: 'AccuFund', note: 'Financial records' },
  { name: 'PERCH', note: 'Ag & environmental projects' },
  { name: 'Spreadsheets / PDFs', note: 'Reporting and documents' },
]

export function SystemLandscape() {
  return (
    <Figure
      label="Eight District systems — Access database, CivicPlus, Windows folders, PaperVision, ArcGIS, AccuFund, PERCH, and spreadsheets and PDFs — each holding part of the land process, converging toward a single LAMS operational context."
      wide
    >
      <div className="landscape">
        <ul className="landscape__systems">
          {CURRENT_SYSTEMS.map((s) => (
            <li key={s.name} className="landscape__system">
              <span className="landscape__name">{s.name}</span>
              <span className="landscape__note">{s.note}</span>
            </li>
          ))}
        </ul>
        <div className="landscape__converge" aria-hidden="true">
          <svg viewBox="0 0 400 60" width="100%" height="60" fill="none" preserveAspectRatio="none">
            {[40, 100, 160, 220, 280, 340].map((x) => (
              <path
                key={x}
                d={`M${x} 0 C ${x} 30, 200 20, 200 52`}
                stroke="var(--river-300)"
                strokeWidth="1.25"
                strokeDasharray="3 4"
              />
            ))}
          </svg>
        </div>
        <div className="landscape__target">
          <LamsIcon size={34} />
          <span className="landscape__target-mark">LAMS</span>
          <span className="landscape__target-note">One operational context</span>
        </div>
      </div>
    </Figure>
  )
}

export function LifecycleContinuity() {
  return (
    <Figure
      label="A single Property context travels through acquisition, land management and disposition, carrying its GIS, documents and work with it."
      wide
    >
      <div className="continuity">
        <div className="continuity__track">
          {['Acquisition', 'Land Management', 'Disposition'].map((stage, i) => (
            <div key={stage} className="continuity__stage">
              <span className="continuity__stage-name">{stage}</span>
              <span className="continuity__stage-index" aria-hidden="true">
                {String(i + 1).padStart(2, '0')}
              </span>
            </div>
          ))}
          <div className="continuity__thread" aria-hidden="true">
            <svg viewBox="0 0 600 40" width="100%" height="40" fill="none" preserveAspectRatio="none">
              <path d="M30 20 H570" stroke="var(--land-500)" strokeWidth="2.5" strokeLinecap="round" />
              {[30, 300, 570].map((x) => (
                <circle key={x} cx={x} cy="20" r="7" fill="var(--surface)" stroke="var(--land-600)" strokeWidth="2.5" />
              ))}
            </svg>
          </div>
          <p className="continuity__thread-label">One Property context</p>
        </div>
        <ul className="continuity__cross">
          {['GIS', 'Documents', 'Work'].map((c) => (
            <li key={c}>{c} — cross-cutting throughout</li>
          ))}
        </ul>
      </div>
    </Figure>
  )
}

export function BeforeAfter() {
  return (
    <Figure label="Before: fragmented data, manual routing, spreadsheets and PDFs, compliance risk. After: a shared record, governed workflows, GIS context, automated documents, reporting and audit.">
      <div className="beforeafter">
        <div className="beforeafter__col beforeafter__col--before">
          <h3 className="beforeafter__head">Today</h3>
          <ul>
            <li>Data fragmented across seven systems</li>
            <li>Workflow routed by moving documents through folders</li>
            <li>Documents and reports assembled by hand</li>
            <li>Reporting from spreadsheets and PDFs</li>
            <li>Risk of missing compliance deadlines</li>
          </ul>
        </div>
        <div className="beforeafter__arrow" aria-hidden="true">
          <svg viewBox="0 0 40 24" width="40" height="24" fill="none">
            <path d="M2 12h32M27 5l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
        <div className="beforeafter__col beforeafter__col--after">
          <h3 className="beforeafter__head">With LAMS</h3>
          <ul>
            <li>One shared land record across the lifecycle</li>
            <li>Governed workflow with approvals and assignment</li>
            <li>Documents generated from the record that holds the facts</li>
            <li>Reporting and dashboards by program, region and period</li>
            <li>Traceable history and audit on every change</li>
          </ul>
        </div>
      </div>
    </Figure>
  )
}

const WEIGHTS = [
  { label: 'Work Plan & Approach', points: 50, tone: 'river' },
  { label: 'Time and Cost Proposal', points: 20, tone: 'land' },
  { label: 'Staff Qualifications', points: 15, tone: 'earth' },
  { label: 'Outcomes and Performance', points: 15, tone: 'ink' },
] as const

export function EvaluationWeights() {
  return (
    <Figure label="Evaluation weighting: Work Plan and Approach 50 points, Time and Cost 20 points, Staff Qualifications 15 points, Outcomes and Performance Measurements 15 points.">
      <ul className="weights">
        {WEIGHTS.map((w) => (
          <li key={w.label} className={`weights__row weights__row--${w.tone}`}>
            <span className="weights__label">{w.label}</span>
            <span className="weights__bar">
              <span className="weights__fill" style={{ width: `${w.points * 2}%` }} />
            </span>
            <span className="weights__points">
              {w.points}
              <span className="weights__unit"> pts</span>
            </span>
          </li>
        ))}
      </ul>
    </Figure>
  )
}

/* ============================ SOLUTION ============================ */

export function ProductConcept() {
  return (
    <Figure label="LAMS sits at the centre of the District land lifecycle, integrating outward to the District's existing enterprise systems rather than replacing them.">
      <Orbit
        centre={{ title: 'LAMS', detail: 'Purpose-built for the SRWMD land lifecycle' }}
        nodes={['ArcGIS', 'Entra ID', 'CivicPlus', 'PaperVision', 'AccuFund', 'PERCH']}
      />
    </Figure>
  )
}

export function ApplicationMap() {
  return (
    <Figure label="LAMS Operations comprises nine applications: Operations Center, Properties, Land Acquisition, Land Management, Disposition, GIS, Work Management, Documents, and Reports and Analytics. Studio and Runtime sit beneath them.">
      <div className="appmap">
        <CardGrid
          columns={3}
          tone="mixed"
          cards={[
            { title: 'Operations Center', detail: 'Home, tasks, alerts, what needs attention' },
            { title: 'Properties', detail: 'The unified land record' },
            { title: 'Land Acquisition', detail: 'Intake through closing' },
            { title: 'Land Management', detail: 'Planning, work, natural resources, timber' },
            { title: 'Disposition', detail: 'Evaluation through closing' },
            { title: 'GIS', detail: 'Workspace and embedded map context' },
            { title: 'Work Management', detail: 'Scheduling, assignment, execution, inspection' },
            { title: 'Documents', detail: 'Generation, templates, PaperVision links' },
            { title: 'Reports & Analytics', detail: 'Program, region and period reporting' },
          ]}
        />
        <div className="appmap__platform">
          <span className="appmap__platform-label">LAMS Studio &amp; Runtime</span>
          <span className="appmap__platform-note">Explained in the Architecture chapter</span>
        </div>
      </div>
    </Figure>
  )
}

const PROPERTY_TABS = ['Overview', 'Location', 'GIS', 'Classification', 'Measures', 'Documents', 'Work', 'History']

export function PropertyWorkspace() {
  return (
    <Figure
      label="Conceptual Property workspace with tabs for Overview, Location, GIS, Classification, Measures, Documents, Work and History, showing a property summary alongside its map context."
      wide
    >
      <MockFrame title="lams.srwmd.local / properties / TR-0142">
        <div className="pw">
          <div className="pw__head">
            <div>
              <span className="pw__eyebrow">Property</span>
              <h3 className="pw__title">TR-0142 · Suwannee River Tract</h3>
            </div>
            <span className="pw__badge">In Management</span>
          </div>
          <nav className="pw__tabs" aria-hidden="true">
            {PROPERTY_TABS.map((t, i) => (
              <span key={t} className={`pw__tab${i === 0 ? ' is-active' : ''}`}>
                {t}
              </span>
            ))}
          </nav>
          <div className="pw__body">
            <dl className="pw__facts">
              {[
                ['County', 'Suwannee'],
                ['Region', 'Lower Suwannee'],
                ['Acreage', '1,284.6'],
                ['Classification', 'Conservation / Riverine'],
                ['Acquired', 'Mar 2019'],
                ['Open work', '4 tasks'],
              ].map(([k, v]) => (
                <div key={k} className="pw__fact">
                  <dt>{k}</dt>
                  <dd>{v}</dd>
                </div>
              ))}
            </dl>
            <div className="pw__map" aria-hidden="true">
              <svg viewBox="0 0 200 140" width="100%" height="100%" preserveAspectRatio="xMidYMid slice">
                <rect width="200" height="140" fill="var(--river-050)" />
                <g stroke="var(--river-300)" strokeOpacity="0.5" fill="none">
                  {Array.from({ length: 7 }, (_, i) => (
                    <line key={i} x1={i * 30} y1="0" x2={i * 30} y2="140" />
                  ))}
                  {Array.from({ length: 5 }, (_, i) => (
                    <line key={`h${i}`} x1="0" y1={i * 30} x2="200" y2={i * 30} />
                  ))}
                </g>
                <path d="M30 30 L120 20 L140 90 L50 105 Z" fill="var(--land-500)" fillOpacity="0.3" stroke="var(--land-600)" strokeWidth="2" />
                <path d="M-5 110 C 40 92, 90 120, 140 100 S 190 70, 210 76" stroke="var(--river-500)" strokeWidth="6" fill="none" strokeLinecap="round" />
              </svg>
              <span className="pw__map-tag">District ArcGIS</span>
            </div>
          </div>
        </div>
      </MockFrame>
    </Figure>
  )
}

export function AcquisitionFlow() {
  return (
    <Figure
      label="Acquisition path: intake, file and GIS triage, prospectus, LAS ranking, committee and board, due diligence, closing, then transition into land management."
      wide
    >
      <Chain
        steps={[
          { label: 'Intake', detail: 'CivicPlus' },
          { label: 'File / GIS triage', detail: 'Triage map' },
          { label: 'Prospectus', detail: 'Site + financial' },
          { label: 'LAS ranking', detail: 'Score sheets' },
          { label: 'Committee / Board', detail: 'Memoranda' },
          { label: 'Due diligence', detail: 'Appraisal, ESA' },
          { label: 'Closing', detail: 'Contracts' },
          { label: 'Management', detail: 'Transition' },
        ]}
      />
    </Figure>
  )
}

export function ManagementFlow() {
  return (
    <Figure
      label="Land management path: three-year planning, GIS program context, schedule and assign, execute, inspect and record, then report."
      wide
    >
      <Chain
        tone="land"
        steps={[
          { label: '3-Year Planning', detail: 'By program area' },
          { label: 'GIS program context', detail: 'Bi-directional' },
          { label: 'Schedule & assign', detail: 'Staff / contractor' },
          { label: 'Execute', detail: 'Field work' },
          { label: 'Inspect / record', detail: 'Measures, ratings' },
          { label: 'Report', detail: 'Monthly, annual' },
        ]}
      />
    </Figure>
  )
}

export function DispositionFlow() {
  return (
    <Figure
      label="Disposition path: initiate, evaluate with GIS, LAS approval, committee and board, due diligence, closing."
      wide
    >
      <Chain
        tone="earth"
        steps={[
          { label: 'Initiate', detail: 'From management' },
          { label: 'Evaluate / GIS', detail: 'Triage map' },
          { label: 'LAS approval', detail: 'Disposal review' },
          { label: 'Committee / Board', detail: 'Memoranda' },
          { label: 'Due diligence', detail: 'Appraisal, contracts' },
          { label: 'Closing', detail: 'Lifecycle complete' },
        ]}
      />
    </Figure>
  )
}

export function GisSnapLayout() {
  return (
    <Figure
      label="Conceptual split layout: the property record occupies the left half of the screen and the District ArcGIS map context the right half, both visible at once."
      wide
    >
      <MockFrame title="lams.srwmd.local / properties / TR-0142 — split view">
        <div className="snap">
          <div className="snap__pane">
            <span className="snap__pane-title">Property record</span>
            <ul className="snap__rows">
              {['Classification', 'Measures — 6 definitions', 'Open work — 4 tasks', 'Documents — 23', 'History'].map((r) => (
                <li key={r}>{r}</li>
              ))}
            </ul>
          </div>
          <div className="snap__divider" aria-hidden="true" />
          <div className="snap__pane snap__pane--map">
            <span className="snap__pane-title">GIS context</span>
            <div className="snap__map" aria-hidden="true">
              <svg viewBox="0 0 200 150" width="100%" height="100%" preserveAspectRatio="xMidYMid slice">
                <rect width="200" height="150" fill="var(--river-050)" />
                <g stroke="var(--river-300)" strokeOpacity="0.45" fill="none">
                  {Array.from({ length: 8 }, (_, i) => (
                    <line key={i} x1={i * 28} y1="0" x2={i * 28} y2="150" />
                  ))}
                  {Array.from({ length: 6 }, (_, i) => (
                    <line key={`h${i}`} x1="0" y1={i * 28} x2="200" y2={i * 28} />
                  ))}
                </g>
                <path d="M40 34 L128 24 L146 96 L58 110 Z" fill="var(--land-500)" fillOpacity="0.32" stroke="var(--land-600)" strokeWidth="2" />
                <path d="M-5 118 C 44 100, 96 128, 146 108 S 196 78, 212 84" stroke="var(--river-500)" strokeWidth="7" fill="none" strokeLinecap="round" />
              </svg>
            </div>
            <span className="snap__legend">Parcel · Natural community · Hydrology</span>
          </div>
        </div>
      </MockFrame>
    </Figure>
  )
}

export function WorkTaskWorkspace() {
  return (
    <Figure
      label="Conceptual work management screen showing a task with its responsible party, due date, related property, attached documents and workflow history."
      wide
    >
      <MockFrame title="lams.srwmd.local / work / WO-2027-0418">
        <div className="task">
          <div className="task__head">
            <div>
              <span className="pw__eyebrow">Work order</span>
              <h3 className="pw__title">Boundary maintenance — TR-0142</h3>
            </div>
            <span className="task__status">Scheduled</span>
          </div>
          <dl className="task__meta">
            {[
              ['Responsible', 'Field Operations — Crew 2'],
              ['Due', '14 May 2027'],
              ['Property', 'TR-0142 · Suwannee River Tract'],
              ['Program', 'Boundary maintenance'],
              ['Contract', 'PO-2027-0091'],
            ].map(([k, v]) => (
              <div key={k}>
                <dt>{k}</dt>
                <dd>{v}</dd>
              </div>
            ))}
          </dl>
          <div className="task__cols">
            <div>
              <span className="task__col-title">Documents</span>
              <ul className="task__list">
                <li>Work authorization.pdf</li>
                <li>Site map — triage.pdf</li>
                <li>Inspection form</li>
              </ul>
            </div>
            <div>
              <span className="task__col-title">History</span>
              <ol className="task__timeline">
                <li>Created from 3-year plan · 2 Apr</li>
                <li>Assigned to Crew 2 · 8 Apr</li>
                <li>Scheduled · 11 Apr</li>
              </ol>
            </div>
          </div>
        </div>
      </MockFrame>
    </Figure>
  )
}

export function DesktopMobile() {
  return (
    <Figure
      label="A desktop layout with record and map side by side, next to two narrow phone layouts showing a task list and a property summary."
      wide
    >
      <div className="devices">
        <div className="devices__desktop">
          <MockFrame title="Desktop — GIS-heavy, multi-context" chrome={false}>
            <div className="devices__desktop-body">
              <div className="devices__pane" />
              <div className="devices__pane devices__pane--map" />
            </div>
          </MockFrame>
          <span className="devices__caption">Desktop-first for GIS and multi-context work</span>
        </div>
        <div className="devices__phones">
          {['Tasks', 'Property'].map((label) => (
            <div key={label} className="devices__phone">
              <span className="devices__phone-title">{label}</span>
              <div className="devices__phone-rows">
                {Array.from({ length: 5 }, (_, i) => (
                  <span key={i} className="devices__phone-row" />
                ))}
              </div>
            </div>
          ))}
          <span className="devices__caption">Responsive and mobile compatible · no native app required</span>
        </div>
      </div>
    </Figure>
  )
}

/* ============================ ARCHITECTURE ============================ */

export function StudioRuntimeOperations() {
  return (
    <Figure
      label="Three layers: LAMS Studio defines governed metadata, LAMS Runtime interprets and enforces it, and LAMS Operations is the District-facing business experience."
    >
      <LayerStack
        layers={[
          {
            title: 'LAMS Studio',
            detail: 'Defines',
            tone: 'river',
            items: ['Application Designer', 'Object Designer', 'Workflows & actions', 'Security', 'AI', 'Publishing'],
          },
          {
            title: 'LAMS Runtime',
            detail: 'Interprets and enforces',
            tone: 'ink',
            items: ['Metadata & object services', 'State & event runtime', 'Automation & policy', 'Query & UI runtime'],
          },
          {
            title: 'LAMS Operations',
            detail: 'Delivers the business experience',
            tone: 'land',
            items: ['React experience composed from governed metadata'],
          },
        ]}
      />
    </Figure>
  )
}

export function PlatformObject() {
  return (
    <Figure label="A platform object at the centre, surrounded by composable capabilities: Auditable, Stateful, GeoEnabled, DocumentEnabled, ApprovalEnabled and AIEnabled.">
      <Orbit
        centre={{ title: 'Platform Object', detail: 'A logical business capability' }}
        nodes={['Auditable', 'Stateful', 'GeoEnabled', 'DocumentEnabled', 'ApprovalEnabled', 'AIEnabled']}
      />
    </Figure>
  )
}

export function EamDiscipline() {
  return (
    <Figure
      label="The EAM chain applied to land: Property, Location, Classification, Attributes and Measures, Work, Documents, History."
      wide
    >
      <Chain
        tone="land"
        emphasis={0}
        steps={[
          { label: 'Property' },
          { label: 'Location' },
          { label: 'Classification' },
          { label: 'Attributes / Measures' },
          { label: 'Work' },
          { label: 'Documents' },
          { label: 'History' },
        ]}
      />
    </Figure>
  )
}

export function EamExpertise() {
  return (
    <Figure label="Three of the five named team members have documented IBM Maximo experience: Jesse Rovira as Software Architect, Bruno Freitas as UX/UI Designer, and Felipe Nonato Marinho as Project Manager.">
      <ul className="experts">
        <li className="experts__card experts__card--lead">
          <span className="experts__role">Software Architect / Technical Lead</span>
          <span className="experts__name">Jesse Rovira</span>
          <ul className="experts__points">
            <li>Founded ID2 Tecnologia on the concept &ldquo;Everything is an Asset&rdquo;, built on IBM Maximo</li>
            <li>IBM &ldquo;Innovation&rdquo; award, 2010 — for applying Maximo beyond physical assets, across 10+ deployments</li>
            <li>Lecturer, IBM Pulse, Las Vegas, February 2011</li>
            <li>Maximo-based solutions for Ministry of Tourism, DATASUS and DNIT</li>
          </ul>
        </li>
        <li className="experts__card">
          <span className="experts__role">UX/UI Designer Sr.</span>
          <span className="experts__name">Bruno Freitas</span>
          <ul className="experts__points">
            <li>IBM Maximo and Control Desk 7.6 developer</li>
            <li>Maximo Asset Management V7.1 — installation, configuration, customization, WSDL/SOAP integration, user training</li>
            <li>IBM Certified Deployment Professional, Maximo Asset Management V7.1 (2011)</li>
            <li>Maximo Application Suite certifications, 2023–2024</li>
          </ul>
        </li>
        <li className="experts__card">
          <span className="experts__role">Project Manager / Scrum Master</span>
          <span className="experts__name">Felipe Nonato Marinho</span>
          <ul className="experts__points">
            <li>Scrum-based government implementations using IBM Maximo and SmartCloud Control Desk</li>
            <li>15+ years delivery, 9+ years Agile and Scrum</li>
            <li>Scrum Master certification, Scrum.org</li>
          </ul>
        </li>
      </ul>
      <p className="experts__disclaimer">
        LAMS is not IBM Maximo and contains no IBM Maximo code or licensing. IBM does not sponsor, certify or endorse
        LAMS. Evidence shown is drawn from the submitted resumes.
      </p>
    </Figure>
  )
}

export function EamModel() {
  return (
    <Figure
      label="Property relationship model. Property connects to Location, Classification, Property Attributes, Property Measures, Work, Documents and Lifecycle History. Classification defines Attribute Definitions and Measure Definitions. Property Measures carry Measure Readings over time."
      wide
    >
      <div className="eam">
        <div className="eam__class">
          <span className="eam__node eam__node--class">Classification hierarchy</span>
          <ul className="eam__defs">
            <li>Attribute Definitions</li>
            <li>Measure Definitions</li>
          </ul>
        </div>
        <div className="eam__down" aria-hidden="true">
          <svg viewBox="0 0 12 28" width="12" height="28" fill="none">
            <path d="M6 0v22M2 18l4 4 4-4" stroke="var(--line-strong)" strokeWidth="1.5" />
          </svg>
        </div>
        <span className="eam__node eam__node--property">Property</span>
        <ul className="eam__children">
          <li>Location</li>
          <li>Property Attributes</li>
          <li className="eam__child--measures">
            Property Measures
            <span className="eam__sub">Measure Reading · time / value / source</span>
          </li>
          <li>GIS context / geometry</li>
          <li>Work</li>
          <li>Documents</li>
          <li>Lifecycle cases / history</li>
        </ul>
        <p className="eam__key">
          <strong>Attributes</strong> describe and qualify. <strong>Measures</strong> carry readings over time, so
          history is never overwritten.
        </p>
      </div>
    </Figure>
  )
}

export function EamLifecycleValue() {
  return (
    <Figure
      label="The same Property context serves every business area. Acquisition establishes Location, Classification, baseline attributes and initial Measures. Land Management evolves characteristics and Measure Readings through inspections, plans and operational work. Disposition reuses the accumulated classification, attributes and reading history. Across all three, GIS reads Properties, Locations and Measures spatially, and reporting and AI use classification, attributes and historical readings as structured, permission-aware information."
      wide
    >
      <div className="eamvalue">
        <Chain
          tone="land"
          steps={[
            { label: 'Acquisition', detail: 'Establishes Location, Classification, baseline attributes and Measures' },
            { label: 'Land Management', detail: 'Evolves characteristics and readings through inspections, plans and work' },
            { label: 'Disposition', detail: 'Reuses the accumulated classification, attributes and reading history' },
          ]}
        />
        <CardGrid
          columns={2}
          tone="river"
          cards={[
            { meta: 'Across the lifecycle', title: 'GIS', detail: 'Properties, Locations and Measures in spatial visualization and analysis' },
            { meta: 'Across the lifecycle', title: 'Reporting and AI', detail: 'Classification, attributes and reading history as structured, permission-aware information' },
          ]}
        />
      </div>
    </Figure>
  )
}

export function TechnicalStack() {
  return (
    <Figure
      label="Technical stack: React and TypeScript frontend over a Node.js and TypeScript API and runtime, over workflow, policy, event, automation, AI and GIS services, over Microsoft SQL Server. External integrations to ArcGIS, Entra ID, CivicPlus, PaperVision, AccuFund and PERCH."
      wide
    >
      <div className="stackfig">
        <LayerStack
          layers={[
            { title: 'React + TypeScript', detail: 'Responsive web application', tone: 'river' },
            { title: 'Node.js + TypeScript', detail: 'API and runtime services', tone: 'river' },
            {
              title: 'Platform services',
              detail: 'Business logic in the service layer',
              tone: 'ink',
              items: ['Workflow', 'Policy', 'Event', 'Automation', 'AI (Gemma + Ollama, local)', 'GIS'],
            },
            { title: 'Microsoft SQL Server', detail: 'District-hosted, on premises', tone: 'land' },
          ]}
        />
        <div className="stackfig__side">
          <span className="stackfig__side-title">External integrations</span>
          <ul className="stackfig__ext">
            {[
              ['ArcGIS', 'ESRI APIs / SDK'],
              ['Entra ID', 'SSO, M365 E3'],
              ['CivicPlus', 'API'],
              ['PaperVision', 'API'],
              ['AccuFund', 'Scheduled CSV'],
              ['PERCH', 'Read-only SQL views'],
            ].map(([name, how]) => (
              <li key={name}>
                <span className="stackfig__ext-name">{name}</span>
                <span className="stackfig__ext-how">{how}</span>
              </li>
            ))}
          </ul>
          <span className="stackfig__deploy">Docker containers on District-provided infrastructure</span>
        </div>
      </div>
    </Figure>
  )
}

export function EventPipeline() {
  return (
    <Figure
      label="Event pipeline: an object change writes a domain event in the same transaction, a persistent job is claimed by a worker, which performs an action or integration, and the outcome is audited."
      wide
    >
      <Chain
        steps={[
          { label: 'Object change', detail: 'Business update' },
          { label: 'Domain event', detail: 'Same SQL transaction' },
          { label: 'Persistent job', detail: 'Durable store' },
          { label: 'Worker', detail: 'Lease · retry · idempotent' },
          { label: 'Action / integration', detail: 'Document, API, AI' },
          { label: 'Audit', detail: 'Correlated history' },
        ]}
      />
    </Figure>
  )
}

export function SecurityChain() {
  return (
    <Figure
      label="Authorization chain: security group, application, application action, execution pipeline, object action, row-level security, field and business policy, then execute and audit."
      wide
    >
      <div className="security">
        <Chain
          steps={[
            { label: 'Security group', detail: 'Entra ID' },
            { label: 'Application', detail: 'May it enter?' },
            { label: 'Application action', detail: 'May it initiate?' },
            { label: 'Object action', detail: 'May it execute?' },
            { label: 'Row-level security', detail: 'Which records?' },
            { label: 'Field / business policy', detail: 'Which fields?' },
            { label: 'Execute + audit', detail: 'Recorded' },
          ]}
        />
        <div className="security__paths">
          <span className="security__paths-title">The same chain governs every path</span>
          <ul>
            {['Screens', 'Reports', 'Exports', 'GIS views', 'Asynchronous work', 'AI retrieval'].map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        </div>
      </div>
    </Figure>
  )
}

export function GisArchitecture() {
  return (
    <Figure label="GIS architecture: LAMS owns the business model, the GIS engine owns spatial semantics behind a provider abstraction, SQL Server provides spatial persistence, and ArcGIS provides the District GIS ecosystem.">
      <LayerStack
        layers={[
          { title: 'LAMS business model', detail: 'GeoEnabled object capability', tone: 'river' },
          { title: 'GIS engine', detail: 'Spatial semantics behind a provider abstraction', tone: 'ink' },
          {
            title: 'Providers',
            detail: 'Interchangeable without redefining objects',
            tone: 'land',
            items: ['SQL Server geometry / geography', 'ArcGIS services and layers', 'PostGIS — extension option, not required'],
          },
        ]}
      />
    </Figure>
  )
}

export function AiPipeline() {
  return (
    <Figure
      label="AI pipeline: an AIEnabled object publishes a semantic profile, which produces a semantic projection, chunks and embeddings; retrieval returns candidates, the policy resolver authorizes, and only then does AI receive context."
      wide
    >
      <div className="ai">
        <Chain
          steps={[
            { label: 'AIEnabled object', detail: 'Declared capability' },
            { label: 'Semantic profile', detail: 'Versioned governance' },
            { label: 'Semantic projection', detail: 'Approved fields only' },
            { label: 'Chunks + embedding', detail: 'Derived index' },
            { label: 'Retrieval candidates', detail: 'Candidates only' },
            { label: 'Policy resolver', detail: 'RLS + field policy' },
            { label: 'Authorized context', detail: 'What AI may see' },
          ]}
        />
        <p className="ai__note">
          Inference runs locally on Gemma via Ollama, inside approved LAMS infrastructure. SQL Server remains the
          business source of truth; embeddings are a derived index.
        </p>
      </div>
    </Figure>
  )
}

export function ApplicationPackage() {
  return (
    <Figure
      label="Publishing lifecycle: draft, validate, publish, then promotion through development, staging and UAT, to production. The application package contains domain, experience, execution, governance and version."
      wide
    >
      <div className="pkg">
        <Chain steps={[{ label: 'Draft' }, { label: 'Validate' }, { label: 'Publish' }]} />
        <Chain
          tone="land"
          steps={[{ label: 'Development' }, { label: 'Staging / UAT' }, { label: 'Production' }]}
        />
        <div className="pkg__contents">
          <span className="pkg__contents-title">Application Package</span>
          <ul>
            {['Domain', 'Experience', 'Execution', 'Governance', 'Version'].map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        </div>
      </div>
    </Figure>
  )
}
